// ===========================================================================
// Lumi signaling protocol.
//
// The single, shared definition of every message exchanged over the WebSocket
// between a Lumi client (web or mobile) and the signaling server. Importing this
// module on both ends keeps the wire format in lockstep.
//
// Transport: JSON text frames, each shaped { t: <type>, ...payload }.
// ASCII only. American English. See docs/TECHNICAL_REFERENCE.md.
// ===========================================================================

/** Protocol version. Bump on any breaking change to the wire format. */
export const PROTOCOL_VERSION = 1;

/**
 * Age bands. The matchmaker pairs strictly within a band, which is how the
 * safety rule is enforced: MINOR users are only ever paired with MINOR users,
 * and ADULT users are never paired with MINOR users.
 */
export const AgeBand = Object.freeze({
  MINOR: 'minor', // under 18
  ADULT: 'adult', // 18 or over
});

/** Chat session modes. */
export const ChatMode = Object.freeze({
  VIDEO: 'video',
  AUDIO: 'audio',
  TEXT: 'text',
});

/** Messages sent by the CLIENT to the SERVER. */
export const ClientMsg = Object.freeze({
  HELLO: 'hello',     // { token, birthYear, mode, caps }
  READY: 'ready',     // {} enter the matchmaking queue
  SIGNAL: 'signal',   // { kind: 'offer'|'answer'|'ice', data } (legacy P2P path)
  SFU: 'sfu',         // { op, reqId?, ...args } request to the media server (SFU)
  CHAT: 'chat',       // { text, id, replyTo? } id is client-generated; replyTo quotes another message
  TYPING: 'typing',   // { state: boolean }
  REACT: 'react',     // { id, emoji } toggle one of REACTION_EMOJI on a message
  UNSEND: 'unsend',   // { id } take back a message YOU sent
  SKIP: 'skip',       // {} request to skip the current peer
  GOBACK: 'goback',   // {} reclaim the just-skipped peer within the grace window
  // "Come and talk to me": ask for a link that pairs somebody straight with
  // you instead of putting them in the queue. See direct-invites.js.
  INVITE_MINE: 'invite_mine', // {} -> ServerMsg.INVITE_LINK
  REPORT: 'report',   // { reason, note? } always allowed, bypasses skip lock
  KICK: 'kick',       // { targetId, reason, note? } moderator ejects a peer for 15 min
  MOD_FRAME: 'mod_frame', // { image } base64 selfie/camera frame for image moderation
  MOD_AUDIO: 'mod_audio', // { audio } base64 audio clip for transcription (moderation + captions)
  LEAVE: 'leave',     // {} leave the session and/or queue
  PONG: 'pong',       // {} heartbeat reply
  // Whether this page is the tab somebody is looking at. Sent on every change,
  // by a visitor socket as well as a session one, so the console can tell a
  // person reading the landing from a tab left open behind other windows.
  // Says nothing about what they are doing, only whether the tab is on screen.
  VISIBLE: 'visible', // { on: boolean }
  ADMIN_LIVE_ANSWER: 'admin_live_answer', // { answer } accept an admin drop-in (SDP answer)
  // Reply to a TEXT drop-in from an administrator. Only accepted while one is
  // open on this session; there is no way for a user to start one.
  ADMIN_CHAT_SAY: 'admin_chat_say',       // { text }
  // ---- Group rooms ----
  CREATE_ROOM: 'create_room',           // { name, visibility, mode } -> ROOM_JOINED
  JOIN_ROOM: 'join_room',               // { roomId?, code? } -> ROOM_JOINED | ROOM_ERROR
  JOIN_RANDOM_ROOM: 'join_random_room', // { mode } -> ROOM_JOINED (joins or creates a public room of that kind)
  LIST_ROOMS: 'list_rooms',             // {} -> ROOM_LIST (public rooms in your band)
  // Staff only: every live room, both bands, private ones included. The server
  // refuses it for anyone whose signed token does not carry the staff flag.
  LIST_ALL_ROOMS: 'list_all_rooms',     // {} -> ROOM_LIST { rooms, all: true }
  LEAVE_ROOM: 'leave_room',             // {} leave the current group room
  GO_PRIVATE_REQUEST: 'go_private_request', // { targetId } ask a group peer to go 1:1
  GO_PRIVATE_RESPOND: 'go_private_respond', // { fromId, accept } answer a 1:1 request
  ROOM_VOTE_START: 'room_vote_start',   // {} start a vote to make the public room private
  ROOM_VOTE_CAST: 'room_vote_cast',     // { yes } cast a privacy vote
  // ---- Shared video (ADULT group rooms only) ----
  // What is shared is the POSITION, not the media: each viewer streams it
  // themselves, and Lumi never fetches or relays a frame of it.
  // The stage: a room invites one person in from the waiting queue for a
  // few minutes. Adults only, host asks, guest decides.
  STAGE_REQUEST: 'stage_request',       // {} host asks for a guest
  STAGE_RESPOND: 'stage_respond',       // { accept } the invited person answers
  VIDEO_PIN: 'video_pin',               // { url } owner pins a video, or clears it
  VIDEO_CONTROL: 'video_control',       // { action: play|pause|seek|stop, atMs }
  VIDEO_SHARE: 'video_share',           // { on } owner lets everyone play/pause
  // ---- Lower-pressure starts ----
  REVEAL: 'reveal',                 // { person?: boolean, off?: true } show my camera; video flows only when both have AND both cameras show a person (person, checked on the device only until the cameras open). off: my camera went off, which closes both
  // Explicit chat between consenting adults (1:1 only). Two separate things:
  // SET_EXPLICIT picks the LANE you are matched into next (you only ever meet
  // others who picked it too); EXPLICIT is your consent WITH THE PERSON IN
  // FRONT OF YOU, and either of you can withdraw it at any moment.
  SET_EXPLICIT: 'set_explicit',     // { on } which lane to be matched into next
  EXPLICIT: 'explicit',             // { on } consent, or withdraw it, for THIS pair
  SET_LANG: 'set_lang',             // { lang: tag|null } the language to read messages in; null turns translation off for this person
  ICEBREAKER_SKIP: 'icebreaker_skip', // {} dismiss the current card (1:1)
  ROOM_STATUS: 'room_status',       // { text } a one-line "what I'm doing" (quiet room)
  FOCUS_START: 'focus_start',       // { minutes: 25|50 } start the room's shared timer
  OBSERVER_HELLO: 'observer_hello', // { id? } "hi, I've been lurking" -- posts a friendly line, leaves observer state
  // ---- Regulars ----
  // { pairId } right after a chat, or { person } later -- the same wave, sent
  // from the list of people you talked to this week. Both honour the seven-day
  // window the site promises.
  WAVE: 'wave',                     // { pairId } wave at the person you just talked to
  REGULARS_LIST: 'regulars_list',   // {} -> REGULARS
  REGULAR_BLOCK: 'regular_block',   // { personId } sever silently
  REGULAR_UNBLOCK: 'regular_unblock', // { personId } lift a block you made; nothing it removed comes back
  PRESENCE_SET: 'presence_set',     // { invisible: boolean, quietHours?: { from, to, offset }|null }
  // ---- Calls between regulars ----
  CALL_INVITE: 'call_invite',       // { personId, mode: 'video'|'audio' }
  CALL_RESPOND: 'call_respond',     // { callId, accept }
  CALL_CANCEL: 'call_cancel',       // { callId }
  // ---- Messages between regulars ----
  MSG_SEND: 'msg_send',             // { to: personId, text, id, slow?: boolean } id is client-generated, echoed back as clientId
  MSG_LIST: 'msg_list',             // { with: personId, before?: iso } -> MSG_LIST
  MSG_DELETE: 'msg_delete',         // { id } hide a message for yourself (both sides -> the row is gone)
  MSG_REPORT: 'msg_report',         // { id, reason, note? } report a message you received; the thread goes with it
  // ---- Communities ----
  COMMUNITY_JOIN: 'community_join', // { code } enter my community's room (materializes it)
  // ---- Activities ----
  ACTIVITY: 'activity',             // { op: 'start'|'stop'|'set'|'event'|'frame', slug?, data? }
  // ---- Watch-together queue ----
  VIDEO_QUEUE: 'video_queue',       // { action: 'add'|'remove'|'skip_vote'|'next', url?, index? }
  VIDEO_REPORT: 'video_report',     // { reason } report the video playing in a teen room: it stops for the room and a report is filed
  // Channels (server/src/visitors.js): a page watches one named by a
  // capability it already holds, on a visitor socket or a session socket
  // alike, and is answered with PUB. No HELLO needed.
  SUB: 'sub',                       // { ch } 'age:<checkId>' | 'support:<requestId>'
  UNSUB: 'unsub',                   // { ch }
});

/** Messages sent by the SERVER to the CLIENT. */
/** How long somebody invited onto a room's stage stays, unless they leave. */
export const STAGE_MS = 10 * 60 * 1000;
/** A room may not ask the queue for somebody more often than this. */
export const STAGE_COOLDOWN_MS = 60 * 1000;

export const ServerMsg = Object.freeze({
  WELCOME: 'welcome',             // { selfId, band, iceServers, config }
  SEARCHING: 'searching',         // { since, alone } alone: nobody else is queued in this band, so the advice for a quiet site is due now rather than in eighteen seconds
  MATCHED: 'matched',             // { pairId, role: 'caller'|'callee', mode, band, peerHandle, selfHandle, ..., peerLang: tag|null, timed: endsAt ms|null, reveal: { me, them, both } }
  SIGNAL: 'signal',               // { kind, data } relayed from the peer (legacy P2P)
  SFU: 'sfu',                     // { op, reqId?, ok?, ...data } media-server reply/event
  CHAT: 'chat',                   // { text, id, replyTo, translated?, lang? } relayed from the peer; translated is the text in the reader's language (lang) when it differs from the writer's
  REACTION: 'reaction',           // { from, id, emoji, on } a reaction toggled on a message (1:1 or room)
  UNSENT: 'unsent',               // { from, id } the sender took a message back
  TYPING: 'typing',               // { state }
  SKIP_UNLOCKED: 'skip_unlocked', // {} the 5s-or-mutual-message rule is satisfied
  // Your own invite code, to be shared. Answered to INVITE_MINE, and again
  // unasked after a reconnect so a page that came back still knows its link.
  INVITE_LINK: 'invite_link',     // { code }
  // What happened to somebody who arrived through one: 'paired' needs no
  // explanation because MATCHED follows, and every other value is a reason
  // said to them in words. See InviteRefusal.
  INVITE_RESULT: 'invite_result', // { ok, reason }
  PEER_LEFT: 'peer_left',         // { reason: 'skip'|'disconnect'|'leave'|'report'|'call_ended' } call_ended: a call between regulars is over; nobody is requeued
  // Their connection died but the chat is being held open for them. `until` is
  // when it stops being held; if they get back before then the pair resumes
  // with a fresh MATCHED carrying resumed:true. A dropped connection used to
  // end a conversation outright, which on a phone changing network meant two
  // people who were talking never found each other again.
  // Who else is here. Sent to every socket whenever it changes, including to
  // somebody still on the landing who has not started anything -- that is the
  // point of it. A site with three people on it and no way to know they are
  // there is a site that feels empty to all three.
  // Or { shown: false } the moment the operator turns the count off.
  LOBBY: 'lobby',                 // { here, waiting, inRooms }
  ROOMS_OPEN: 'rooms_open',       // { rooms: [...], open: [slug] } -- the front-page list, pushed
  // The site-wide banner: on connect, and whenever the operator changes it.
  // An empty text takes it down.
  ANNOUNCEMENT: 'announcement',   // { text }
  // A channel a visitor socket subscribed to (ClientMsg.SUB) has changed.
  PUB: 'pub',                     // { ch, data }
  // Whether "Chat with an Admin" is open: on connect, and when the operator
  // turns it on or off.
  SUPPORT: 'support',             // { enabled }
  // The signed-in account, as /api/account/me says it, the moment anything
  // about it changes: a Plus grant, a purchase, a badge. To every socket the
  // account holds, a visitor socket that offered the account token included.
  ACCOUNT: 'account',             // { account }
  // This device was signed out: of the account (what: 'account') or of a
  // named keepsake (what: 'keepsake'), by its owner elsewhere, a password
  // change or reset, or one sign-in too many. Then the socket closes. The page
  // asks who it is now rather than trusting this (server devices.js).
  SIGNED_OUT: 'signed_out',       // { what, reason }
  ROOM_CLOSING: 'room_closing',   // { text, seconds, at } -- closing soon, with a countdown
  PEER_DROPPED: 'peer_dropped',   // { until: ms, seconds }
  PEER_RETURNED: 'peer_returned', // {} they made it back; a MATCHED follows
  GOBACK_OFFER: 'goback_offer',   // { until } you may reclaim the peer until <until>
  GOBACK_FAILED: 'goback_failed', // { reason }
  BANNED: 'banned',               // { reason, until } refused at connect or ejected
  CAPTION: 'caption',             // { from, handle, text, lang } a partner's transcribed speech
  SYSTEM: 'system',               // { text } a system notice (e.g. a blocked message)
  INTEREST_CLEARED: 'interest_cleared', // {} your interests box was refused or cleared by a moderator: empty it, here and in storage
  ERROR: 'error',                 // { code, message }
  PING: 'ping',                   // {} heartbeat
  ADMIN_LIVE_OFFER: 'admin_live_offer', // { offer, ice, mode } an administrator asks to talk live (consent required); mode is what THEY send: 'video' (on camera) or 'audio' (voice only)
  // A TEXT drop-in: an administrator typing to someone who is waiting to be
  // paired. Needs no camera, no microphone and no consent prompt, because
  // nothing is captured -- it is a message arriving, and the user can simply
  // not answer. `open` says whether the conversation is still live.
  ADMIN_CHAT: 'admin_chat',             // { text?, open }
  FILE_SHARED: 'file_shared',     // { shareId, mime, name, size, from } a file cleared moderation and can be fetched
  // ---- Group rooms ----
  ROOM_JOINED: 'room_joined',         // { roomId, name, visibility, code, ownerId, members:[peerId], mode }
  ROOM_LIST: 'room_list',             // { rooms: [{ id, name, members, visibility, mode }] }
  ROOM_PEER_JOINED: 'room_peer_joined', // { peerId }
  ROOM_PEER_LEFT: 'room_peer_left',   // { peerId }
  ROOM_VIDEO_SLOTS: 'room_video_slots', // { used, max, holders: [peerId] } who is on camera; sent on every change
  ROOM_CHAT: 'room_chat',             // { from, handle, text, id, replyTo, translated?, lang? } a group message; translated as on CHAT
  ROOM_ERROR: 'room_error',           // { error }
  // Ownership moved -- the owner left, and it passed to whoever has been in the
  // room longest. Without this the room would be left naming somebody gone.
  ROOM_OWNER: 'room_owner',           // { ownerId, handle }
  // The room's shared video, in FULL on every change. Sending the whole state
  // rather than a delta means a client that missed one message is still right.
  ROOM_VIDEO: 'room_video',           // { videoId, playing, atMs, sharedControl, by }
  STAGE_OFFER: 'stage_offer',         // { roomName, people, seconds } would you join?
  STAGE_ENDED: 'stage_ended',         // { reason } the guest's time is up
  STAGE_STATE: 'stage_state',         // { guestId, handle, until } told to the room
  // A link in a message turned out to be one we preview. Arrives AFTER the
  // message, because fetching it takes a moment and a message must never wait
  // on a third party to be delivered.
  LINK_PREVIEW: 'link_preview',       // { id, url, host, title, description }
  PRIVATE_REQUEST: 'private_request',   // { fromId, fromHandle } a peer wants to go 1:1
  PRIVATE_DECLINED: 'private_declined', // { byHandle } your 1:1 request was declined
  ROOM_VOTE_STARTED: 'room_vote_started', // { byHandle, until, total } a privacy vote opened
  ROOM_VOTE_UPDATE: 'room_vote_update',   // { yes, total } running tally
  ROOM_VOTE_RESULT: 'room_vote_result',   // { passed } room is now private / vote failed
  // ---- Lower-pressure starts ----
  REVEAL_STATE: 'reveal_state',     // { me: boolean, them: boolean, both: boolean, unseen?: { me, them }, closed?: true } unseen: whose camera did not show a person yet; closed: an open pair's cameras were closed by a camera going off
  EXPLICIT_STATE: 'explicit_state', // { me, them, both } consent in THIS pair; both false again the moment either withdraws
  ICEBREAKER: 'icebreaker',         // { text, deck } a card, after twenty seconds of silence (1:1)
  TIMED_STATE: 'timed_state',       // { endsAt, warnAt } a ten-minute chat's clock
  QUEUE_MEDIA: 'queue_media',       // { on } while waiting: send (or stop sending) camera + mic to the SFU for a moderator's live look. Draws nothing.
  TIMED_END: 'timed_end',           // { canWave: boolean, pairId } the gentle end
  // ---- Pinned rules, the quiet room, and observer mode ----
  // The pinned card, in FULL: sent on join and again on the hour when the
  // question rotates. Rules are the room's own card or the default one.
  ROOM_PIN: 'room_pin',             // { rules: [string], question: string|null, lang: string|null }
  ROOM_PEER_STATUS: 'room_peer_status', // { peerId, text } a member's one-line status ('' clears it)
  // The shared timer, in full on every change; until is null when none runs.
  ROOM_FOCUS: 'room_focus',         // { until: ms|null, minutes, by }
  ROOM_OBSERVERS: 'room_observers', // { watching: n } how many members are here and have not yet spoken
  // ---- Regulars ----
  // The two of you are already regulars, in the chat you are in now. Sent
  // shortly after MATCHED (the answer is a lookup, so it cannot ride along
  // with it), and only when the answer is yes. The client stops offering a
  // wave for this pair and says so in the chat: being asked to wave at
  // somebody you waved at last week reads as the site having forgotten, and
  // there was nothing anywhere in the chat that said otherwise (the operator,
  // 2026-09-21).
  PAIR_REGULARS: 'pair_regulars',   // { pairId, regular: true }
  WAVED: 'waved',                   // { pairId, ok, reason? } your wave was recorded (nobody else is told)
  // note/noteAt: the private note THIS person keeps about that regular (regular-notes.js); never the other way round.
  REGULARS: 'regulars',             // { regulars: [{ personId, handle, since, around: {roomId, name, code?}|null, connected, unread, note: string|null, noteAt: ISO|null }], invisible, quietHours: { from, to, offset }|null, recent: [{ personId, handle, endedAt, expiresAt, waved }], blocked: [{ personId, handle, at }] }
  NOTIFY: 'notify',                 // { kind: 'regular_new'|'regular_around'|'session_soon'|'message'|'call'|'community_ask', text, roomId?, code?, personId?, at }
  STAFF_MSG: 'staff_msg',             // { id, from: 'staff', text, at } a line from Lumi Staff, to every open tab of the person it is for (shared/staff-inbox.js)
  // ---- Calls between regulars ----
  CALL_OFFER: 'call_offer',         // { callId, fromHandle, fromPersonId, mode, until }
  CALL_STATE: 'call_state',         // { callId, state: 'ringing'|'declined'|'expired'|'cancelled'|'connecting'|'busy' }
  // ---- Messages between regulars ----
  // No read receipt and no typing indicator exist here, for anyone: the
  // simplest rule is also the teen rule. A message is delivered, or it waits.
  MSG: 'msg',                       // { id, from, to, text, at, slow, deliverAt, clientId? } one message (live delivery; clientId only on the sender's own copy)
  MSG_LIST: 'msg_list',             // { with, messages: [MSG...], cap: { newToday, max } }
  MSG_DELETED: 'msg_deleted',       // { id, by: 'sender'|'recipient' } a side hid the message for themselves
  // ---- Communities ----
  // The community a room belongs to, in FULL, to each member on join and on
  // every membership or session change. `members` carries the owner (state
  // 'owner') and the accepted members; the owner alone also sees who is
  // invited and who has asked. `here` and `peerId` say who is in the room now.
  COMMUNITY_STATE: 'community_state', // { id, name, code, mode, capacity, members: [{ personId, handle, state, here, peerId }], sessions: [{ id, at, minutes, note, recurrence }], isOwner, you }
  // ---- Activities ----
  // The room's activity, in FULL on every change and to a member on join;
  // slug is null when none is running. The server relays and never plays.
  ACTIVITY_STATE: 'activity_state', // { slug|null, hostId, state, participants: [peerId], startedAt } in full, on every change
  ACTIVITY_EVENT: 'activity_event', // { slug, from, data } a relayed event
  // ---- Watch-together queue ----
  ROOM_VIDEO_QUEUE: 'room_video_queue', // { queue: [{url, by, provider}], dj: peerId|null, skipVotes: n, needed: n }
});

/** Group-room visibility. */
export const RoomVisibility = Object.freeze({
  PUBLIC: 'public',   // listed in the directory; anyone in the band can join
  PRIVATE: 'private', // unlisted; joinable only with the room code
});

/** Group-room limits.
 *
 * Membership and cameras are different budgets. A room is a place people are
 * in, and on the SFU a listener costs the server one small audio consumer per
 * speaker. A camera is not cheap: every member downloads every camera, so
 * four cameras is 4 x ~450 kbps for each viewer, and at twenty members that
 * is ~36 Mbps of server egress for one room (a Room Pro room, 50 people and
 * six cameras, reaches ~135 Mbps -- it was 150 and eight until 2026-09-09,
 * which was ~540 Mbps and cost more per hour than the subscription).
 * So membership is wide and the cameras are metered:
 * VIDEO_SLOTS is how many people can be on camera at once in a free room,
 * ROOM_PRO_VIDEO_SLOTS in a paid one, first come first served. A slot frees
 * the moment its holder turns the camera off or leaves, and whoever is
 * waiting with a camera on takes it. */
export const MAX_ROOM_MEMBERS = 20;
export const VIDEO_SLOTS = 4; // cameras at once in a free room
export const ROOM_PRO_VIDEO_SLOTS = 6; // cameras at once in a Room Pro room
/** Per-camera ceiling in a group room. Tiles are small; this is plenty. */
export const GROUP_VIDEO_MAX_BITRATE = 450_000;
export const ROOM_NAME_MAX = 40;
export const ROOM_CODE_LENGTH = 6;

/** Optional display handle (what others call you). Not an account -- just a label. */
export const HANDLE_MAX = 24;

/** The one line an observer can post with a tap to stop being one. FIXED text,
 *  shared by client and server: the client shows its own copy at once, and the
 *  server posts exactly this through the ordinary chat path, so it is
 *  moderated and transcripted like anything typed. */
export const OBSERVER_HELLO_TEXT = 'Hi, I have been quietly listening';

/** Sanitize a user-supplied handle: printable characters only, trimmed, capped. */
export function cleanHandle(name) {
  if (typeof name !== 'string') return '';
  let out = '';
  for (const ch of name) {
    const code = ch.codePointAt(0);
    if (code >= 32 && code !== 127) out += ch; // drop control characters
  }
  return out.trim().slice(0, HANDLE_MAX);
}

// ---- Monetization catalog (ADULT-ONLY) ----
// Display values for the client. The processor (Stripe) holds the real prices via
// Price IDs; minors never reach any of this (account creation requires 18+).
export const PLUS_PRICES = Object.freeze({ monthlyCents: 499, yearlyCents: 4999 });
export const ROOM_PRO_PRICE_CENTS = 999; // per month
export const ROOM_PRO_CAPACITY = 50; // the raised member cap for a Pro room (and ROOM_PRO_VIDEO_SLOTS cameras)
export const COSMETICS = Object.freeze([
  { id: 'orb-aurora', name: 'Aurora orb', priceCents: 299, kind: 'orb' },
  { id: 'orb-ember', name: 'Ember orb', priceCents: 299, kind: 'orb' },
  { id: 'orb-mono', name: 'Monochrome orb', priceCents: 199, kind: 'orb' },
  { id: 'theme-midnight', name: 'Midnight accent', priceCents: 199, kind: 'theme' },
  { id: 'theme-rose', name: 'Rose accent', priceCents: 199, kind: 'theme' },
]);

/** Reasons used in report messages. */
export const ReportReason = Object.freeze({
  NUDITY: 'nudity',
  MINOR_SAFETY: 'minor_safety',
  HARASSMENT: 'harassment',
  VIOLENCE: 'violence',
  HATE: 'hate',
  SPAM: 'spam',
  OTHER: 'other',
});

/** WebRTC signal kinds carried inside SIGNAL messages (legacy P2P path). */
export const SignalKind = Object.freeze({
  OFFER: 'offer',
  ANSWER: 'answer',
  ICE: 'ice',
});

/**
 * SFU (mediasoup) operations carried inside SFU messages. Media is relayed
 * through the server's Selective Forwarding Unit, so peers connect only to the
 * SFU (their IPs are never exposed to each other) and the server can observe
 * media for the minor-safety moderation policy. Client->server ops carry a reqId
 * the server echoes on the reply; NEW_PRODUCER / PRODUCER_CLOSED are unsolicited
 * server events.
 */
export const SfuOp = Object.freeze({
  JOIN: 'join',                     // -> { rtpCapabilities, producers: [{producerId, kind}] }
  CREATE_TRANSPORT: 'createTransport', // { direction } -> { id, iceParameters, iceCandidates, dtlsParameters }
  CONNECT_TRANSPORT: 'connectTransport', // { transportId, dtlsParameters } -> { ok: true }
  PRODUCE: 'produce',               // { transportId, kind, rtpParameters } -> { id }
  CONSUME: 'consume',               // { producerId, rtpCapabilities } -> { id, producerId, kind, rtpParameters }
  RESUME_CONSUMER: 'resumeConsumer', // { consumerId } -> { ok: true }
  CLOSE_PRODUCER: 'closeProducer',   // { producerId } -> { ok: true } (a camera going off frees its video slot)
  NEW_PRODUCER: 'newProducer',      // (event) { producerId, kind, peerId }
  PRODUCER_CLOSED: 'producerClosed', // (event) { producerId }
  // ---- SCTP data channels (file transfer) ----
  PRODUCE_DATA: 'produceData',      // { transportId, sctpStreamParameters, label, protocol } -> { id }
  CONSUME_DATA: 'consumeData',      // { dataProducerId } -> { id, dataProducerId, sctpStreamParameters, label, protocol }
  NEW_DATA_PRODUCER: 'newDataProducer', // (event) { dataProducerId, peerId, label }
});

/** Error codes the server may return. */
export const ErrorCode = Object.freeze({
  BAD_TOKEN: 'bad_token',
  BAD_ORIGIN: 'bad_origin',
  BAD_MESSAGE: 'bad_message',
  RATE_LIMITED: 'rate_limited',
  SKIP_LOCKED: 'skip_locked',
  NOT_IN_SESSION: 'not_in_session',
  AGE_REQUIRED: 'age_required',
  UNDER_AGE: 'under_age',
  SERVER_FULL: 'server_full',
  // A minor in ten-minute mode has met a lot of people this hour (see
  // TIMED_MINOR_CONTACTS_PER_HOUR). Kind copy, and the queue is not entered.
  SLOW_DOWN: 'slow_down',
  // One place in the queue, and one live 1:1, per person (matchmaker _enqueue).
  SEARCHING_ELSEWHERE: 'searching_elsewhere', // this tab's search moved to their newer tab
  IN_ANOTHER_CHAT: 'in_another_chat',         // they are in a 1:1 in another tab already
  // A social feature is switched off for this band (settings.allowsSocial).
  NOT_AVAILABLE: 'not_available',
  // A pair's camera opens only once BOTH have tapped "Show my camera". The SFU
  // answers a video produce with this until then; it is a state, not a fault.
  REVEAL_REQUIRED: 'reveal_required',
  // Calls and messages between regulars. The caller's own problems are
  // ERRORs; anything about the other person is a CALL_STATE, and says as
  // little as possible. NOT_REGULARS is also the answer for a blocked pair,
  // so a block is never revealed by the shape of the refusal.
  NOT_REGULARS: 'not_regulars',
  BUSY_SELF: 'busy_self', // you are in a chat or a room; leave it first
  ALREADY_CALLING: 'already_calling', // one outstanding invite per caller
  // Five new conversations a day, and this would be the sixth.
  DAILY_CAP: 'daily_cap',
});

// ----- Timing and policy constants (single source of truth) -----------------

/** Skip is locked until this many milliseconds after connection... */
export const SKIP_UNLOCK_MS = 5000;
/** ...unless both peers have sent at least this many messages first. */
export const SKIP_UNLOCK_MESSAGES = 1;
/** Window to go back to a just-skipped peer. */
// Ten seconds, not three. Three was long enough for the ring to finish before
// somebody had finished reading "Skipped by mistake?", let alone found the
// button (the operator, 2026-09-16). The skipper is held out of matching for
// this long, so it is also how long the person they skipped waits; ten seconds
// is a pause, not a stall.
export const GOBACK_WINDOW_MS = 10_000;
/**
 * How long the skipper is held out of matching after a skip, so a go-back in
 * the first moments is reliable.
 *
 * NOT the window above, which is how long the OFFER stands. They were one
 * number, so lengthening the offer to ten seconds also made every skip a
 * ten-second wait for the next person (2026-09-17). Three seconds is what the
 * site did before; after it the skipper queues as normal, the offer is still
 * good, and a go-back still says so when the other person has been taken.
 */
export const GOBACK_REQUEUE_MS = 3000;
/** Heartbeat interval and timeout. */
export const HEARTBEAT_INTERVAL_MS = 25000;
export const HEARTBEAT_TIMEOUT_MS = 60000;
/**
 * How long a 1:1 is held open for a person whose socket died, so the same
 * person can step back into it (server: matchmaker.js). The client keeps its
 * media up for exactly this long after a drop and no longer: the server still
 * knows the pair for the whole of it, and not a moment past it.
 */
export const RECONNECT_GRACE_MS = 45000;
/** Cooldown that prevents two users who just unpaired from instantly rematching. */
export const SEEN_COOLDOWN_MS = 90000;

// Allowed top-level MIME categories for shared files. Images ONLY, deliberately:
// every shared file is classified before anyone can fetch it, and the only thing
// the self-hosted moderator can actually look at is a still image. Video and
// audio would have to be decoded and sampled to be screened at all, which means
// running a media decoder over whatever a stranger uploads -- a large attack
// surface bolted on to serve a small feature. They stay out.
export const ALLOWED_MEDIA_PREFIXES = Object.freeze(['image/']);
/**
 * Types that start with an allowed prefix and are still not pictures.
 *
 * An SVG is a document, not an image: it carries <script>, and a stored share
 * is served back inline with the type the sender declared. Opening one would
 * run the sender's script on the API origin. XML and HTML are here for the
 * same reason -- anything a browser will parse rather than decode.
 */
export const REFUSED_MEDIA_TYPES = Object.freeze([
  'image/svg+xml',
  'image/svg',
  'image/xml',
  'text/html',
  'application/xhtml+xml',
]);
/** Maximum shareable file size (bytes). */
export const MAX_SHARE_BYTES = 25 * 1024 * 1024;

// The reaction set. Deliberately small and FIXED: a reaction reaches the other
// person without passing the text moderator, so it must not be able to carry
// arbitrary content -- an open field here would be an unmoderated chat channel.
// Written as code points to keep the source ASCII.
export const REACTION_EMOJI = Object.freeze([
  String.fromCodePoint(0x1f44d), // thumbs up
  String.fromCodePoint(0x2764, 0xfe0f), // red heart
  String.fromCodePoint(0x1f602), // tears of joy
  // Asked for by the operator (2026-10-04): what people actually send when
  // something is very funny. The slots above stay where they were.
  String.fromCodePoint(0x1f480), // skull
  String.fromCodePoint(0x1f62d), // loudly crying
  String.fromCodePoint(0x1f62e), // astonished
  String.fromCodePoint(0x1f622), // crying
  String.fromCodePoint(0x1f64f), // folded hands
  String.fromCodePoint(0x1f525), // fire
  String.fromCodePoint(0x1f44f), // clapping
]);

/** A client-generated message id: short, opaque, and bounded. */
export const MAX_MSG_ID_LEN = 24;
/** How much of a quoted message travels with a reply. */
export const MAX_QUOTE_LEN = 140;

/** Whether a value is a usable message id. */
/**
 * The things that are not messages but can still be answered: the room's
 * question of the hour, and the icebreaker offered in a 1:1.
 *
 * A reply carries its quoted text with it -- a room member who joined later
 * never saw the original and has nothing to look it up in -- so a prompt needs
 * an id only to satisfy isMessageId, and never has to resolve to anything.
 * Stable strings rather than generated ones, so the same question quoted by
 * two people reads as the same question.
 */
export const PROMPT_IDS = Object.freeze({ question: 'room-question', icebreaker: 'icebreaker' });

/** The label a quoted prompt is attributed to. Kept under the server's 24. */
export const PROMPT_WHO = Object.freeze({ question: 'Question of the hour', icebreaker: 'Icebreaker' });

export function isMessageId(id) {
  return typeof id === 'string' && id.length > 0 && id.length <= MAX_MSG_ID_LEN && /^[A-Za-z0-9_-]+$/.test(id);
}

/**
 * How loud a system message is. Chosen by an administrator when they send one,
 * and fixed to this list so a level can never be free text arriving from a
 * client and turned into a class name.
 *
 *   info    -- grey. The default, and what every automatic notice uses.
 *   good    -- green. General news; nothing is wrong.
 *   caution -- amber. Pay attention; something may affect you.
 *   alert   -- red. Something is wrong.
 */
export const SystemLevel = Object.freeze({
  INFO: 'info',
  GOOD: 'good',
  CAUTION: 'caution',
  ALERT: 'alert',
});

export const SYSTEM_LEVELS = Object.freeze(Object.values(SystemLevel));

/** A level, or 'info' for anything unrecognized. Never trusts its input. */
export function cleanSystemLevel(level) {
  return SYSTEM_LEVELS.includes(level) ? level : SystemLevel.INFO;
}

/** How long a system notice stays pinned once it would scroll away. */
export const SYSTEM_PIN_MS = 30_000;

/** Minimum age to use Lumi at all. Under-13s are never admitted (COPPA-aligned). */
export const MIN_AGE = 13;

/**
 * The age in years implied by a birth year, or null if the year is invalid. Uses
 * the calendar-year convention: born in (currentYear - N) is treated as N years old.
 * @param {number} birthYear
 * @param {number} currentYear
 * @returns {number|null}
 */
export function ageFromBirthYear(birthYear, currentYear) {
  if (!Number.isInteger(birthYear)) return null;
  if (birthYear < 1900 || birthYear > currentYear) return null;
  return currentYear - birthYear;
}

/**
 * Compute the age band from a birth year, given the current year. Returns null for
 * an invalid year OR for anyone under MIN_AGE (they are not admitted at all). The
 * boundary between bands is 18: someone turning 18 this calendar year is ADULT. The
 * authoritative computation happens on the server; the client mirrors it.
 *
 * @param {number} birthYear
 * @param {number} currentYear
 * @returns {('minor'|'adult'|null)} band, or null if invalid or under MIN_AGE
 */
export function ageBandFromBirthYear(birthYear, currentYear) {
  const age = ageFromBirthYear(birthYear, currentYear);
  if (age === null || age < MIN_AGE) return null;
  return age >= 18 ? AgeBand.ADULT : AgeBand.MINOR;
}

/**
 * The whole types a shared file may have: the picture formats the server can
 * recognize from their bytes. Whole types rather than a prefix, because
 * "starts with image/" admits SVG, which is a document, and anything else a
 * browser decides to call an image. The server takes a new TIFF only when
 * every tag in it lays out its picture, and a TIFF shared before it began reading
 * types can still be fetched.
 */
const SHAREABLE_TYPES = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/avif',
  'image/bmp',
  'image/tiff',
]);

/**
 * Decide whether a file may be shared, by MIME type and size.
 * @param {string} mime
 * @param {number} bytes
 * @returns {boolean}
 */
export function isShareableFile(mime, bytes) {
  if (typeof mime !== 'string') return false;
  if (!Number.isFinite(bytes) || bytes <= 0 || bytes > MAX_SHARE_BYTES) return false;
  // A comma anywhere is refused: to a browser reading a Content-Type it is a
  // list separator and the last entry wins, so "image/png;x=1,text/html" is
  // a page. Splitting on the semicolon alone let that through. Then any
  // parameters ("; charset=...") are dropped, and the whole type has to be
  // one of the list.
  if (mime.includes(',')) return false;
  const type = mime.toLowerCase().split(';')[0].trim();
  return SHAREABLE_TYPES.includes(type);
}

/**
 * Whether skip is currently permitted for a pair.
 * @param {object} s
 * @param {number} s.connectedAt   epoch ms when the pair connected
 * @param {number} s.now           current epoch ms
 * @param {number} s.msgsFromA     messages sent by peer A
 * @param {number} s.msgsFromB     messages sent by peer B
 * @returns {boolean}
 */
export function isSkipAllowed({ connectedAt, now, msgsFromA, msgsFromB }) {
  const elapsedOk = now - connectedAt >= SKIP_UNLOCK_MS;
  const mutualOk =
    msgsFromA >= SKIP_UNLOCK_MESSAGES && msgsFromB >= SKIP_UNLOCK_MESSAGES;
  return elapsedOk || mutualOk;
}

// ----- Ten-minute chats (C3) ------------------------------------------------
// Both people opt in on the landing; the server pairs timed with timed only, so
// nobody is put on a clock they did not ask for.

/** How long a ten-minute chat runs, from the moment the pair connects. */
export const TIMED_CHAT_MS = 10 * 60 * 1000;
/** The "thirty seconds left" notice arrives this long before the end. */
export const TIMED_WARN_MS = 30 * 1000;
/**
 * A bounded chat raises the rate at which somebody meets new people -- what
 * the regulator calls scatter-gun contact -- so a minor in timed mode meets at
 * most this many people per rolling hour. Adults are not capped here.
 */
export const TIMED_MINOR_CONTACTS_PER_HOUR = 12;
export const TIMED_CONTACT_WINDOW_MS = 60 * 60 * 1000;
